/**
 * One-time newsletter opt-in prompt.
 *
 * Backs the homepage banner shown to users who registered before the
 * newsletter checkbox existed, so they never had a chance to opt in.
 *
 * Two user-visible outcomes, both permanent (stored on the user's profile,
 * so the choice follows them across devices — not localStorage):
 *   - accept:  privateData.acceptsMarketing = true  + prompt dismissed, then
 *              the server subscribes them to Mailchimp (and writes the GDPR
 *              consent log entry).
 *   - dismiss: prompt dismissed only. We deliberately do NOT write
 *              `acceptsMarketing: false` — declining a prompt isn't the same
 *              as explicitly opting out, and leaving it unset keeps the
 *              profile-settings checkbox honest.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import { denormalisedResponseEntities } from '../util/data';
import { subscribeToNewsletter } from '../util/api';

import { setCurrentUser } from './user.duck';

// Must mirror what user.duck requests when it fetches currentUser. We push
// the response straight into the store via setCurrentUser, so any variant we
// forget to ask for simply vanishes from the store — which is what made the
// avatar fall back to initials after dismissing: `profileImage` was included
// but with no renderable variants attached.
const PROFILE_FIELDS = {
  expand: true,
  include: ['profileImage'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.square-xsmall',
    'variants.square-xsmall2x',
  ],
};

// Persist the profile change, then push the updated user into the store so
// the banner's own visibility check re-evaluates immediately.
const updateProfile = async (sdk, dispatch, privateData) => {
  const response = await sdk.currentUser.updateProfile({ privateData }, PROFILE_FIELDS);
  const entities = denormalisedResponseEntities(response);
  if (entities.length === 1) {
    dispatch(setCurrentUser(entities[0]));
  }
};

export const acceptNewsletterPromptThunk = createAsyncThunk(
  'newsletterPrompt/accept',
  async (_arg, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      await updateProfile(sdk, dispatch, {
        acceptsMarketing: true,
        newsletterPromptDismissed: true,
      });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
    // Synchronous subscribe so the banner only claims success once Mailchimp
    // has actually accepted. On failure we roll the consent flag back, so the
    // profile never says "subscribed" when the user isn't.
    try {
      await subscribeToNewsletter({ wait: true, source: 'banner' });
      return true;
    } catch (e) {
      // Roll the flags back to UNSET, not to `false`. The user tried to opt
      // in — recording an explicit opt-out would be wrong, and it would also
      // make them ineligible for the prompt, so they could never retry.
      try {
        await updateProfile(sdk, dispatch, {
          acceptsMarketing: null,
          newsletterPromptDismissed: null,
        });
      } catch (rollbackError) {
        // Best effort — the error below is what the user sees either way.
      }
      // Surface the API error code so the banner can tell a permanently
      // deleted ("forgotten") address apart from a transient failure.
      return rejectWithValue({ ...storableError(e), apiError: e?.error });
    }
  }
);

export const dismissNewsletterPromptThunk = createAsyncThunk(
  'newsletterPrompt/dismiss',
  async (_arg, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      await updateProfile(sdk, dispatch, { newsletterPromptDismissed: true });
      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const acceptNewsletterPrompt = () => dispatch =>
  dispatch(acceptNewsletterPromptThunk()).unwrap();
export const dismissNewsletterPrompt = () => dispatch =>
  dispatch(dismissNewsletterPromptThunk()).unwrap();

/**
 * Selector — should the one-time prompt be shown to this user?
 *
 * All three must hold:
 *   1. signed in
 *   2. no newsletter preference on record (registered before the checkbox
 *      existed) — an explicit true OR false both count as "already decided"
 *   3. hasn't already accepted or dismissed the prompt
 */
export const selectShouldShowNewsletterPrompt = state => {
  const isAuthenticated = !!state?.auth?.isAuthenticated;
  const currentUser = state?.user?.currentUser;
  if (!isAuthenticated || !currentUser?.id) {
    return false;
  }
  const privateData = currentUser.attributes?.profile?.privateData || {};
  const hasPreference = typeof privateData.acceptsMarketing === 'boolean';
  return !hasPreference && privateData.newsletterPromptDismissed !== true;
};

const newsletterPromptSlice = createSlice({
  name: 'newsletterPrompt',
  initialState: { inProgress: false, error: null, subscribed: false },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(acceptNewsletterPromptThunk.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(acceptNewsletterPromptThunk.fulfilled, state => {
        state.inProgress = false;
        state.subscribed = true;
      })
      .addCase(acceptNewsletterPromptThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      })
      .addCase(dismissNewsletterPromptThunk.pending, state => {
        state.inProgress = true;
      })
      .addCase(dismissNewsletterPromptThunk.fulfilled, state => {
        state.inProgress = false;
      })
      .addCase(dismissNewsletterPromptThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      });
  },
});

export default newsletterPromptSlice.reducer;
